import type {
  Discipline,
  PMIssue,
  ScheduleSnapshotPayload,
  SnapshotIssue,
} from "@/domains/pm/types";
import { daysBetween } from "./ganttScale";

/**
 * What has changed since a snapshot was taken.
 *
 * Both sides are already in the client — the snapshot payload and the loaded
 * issues — so this needs no SQL. Matching is by issue id: issue numbers are
 * stable but an issue can be renamed, and a title match would call a rename a
 * deletion plus an addition.
 */
export type SnapshotChangeKind = "moved" | "added" | "removed" | "unchanged";

export interface SnapshotDiffEntry {
  issue_id: number;
  issue_number: number;
  title: string;
  kind: SnapshotChangeKind;
  snapshotStart: string | null;
  snapshotDue: string | null;
  currentStart: string | null;
  currentDue: string | null;
  /** Days the DUE DATE moved — when the work finishes is what a schedule
   *  slip means. Positive is later. Falls back to the start date when an
   *  issue has no due date (its bar ends there). Null when either side is
   *  unscheduled, or the issue only exists on one side. */
  deltaDays: number | null;
  /**
   * Days the bar got LONGER. Positive is longer.
   *
   * Separate from `deltaDays` because an issue whose start alone moved is
   * `moved` with `deltaDays: 0` — the due date did not travel. Without this a
   * task that gained four days at its front reports as changed and then
   * shows nothing.
   */
  durationDeltaDays: number | null;
}

export interface SnapshotDiff {
  entries: SnapshotDiffEntry[];
  moved: number;
  added: number;
  removed: number;
  unchanged: number;
  completion: {
    snapshot: string | null;
    current: string | null;
    deltaDays: number | null;
  };
}

/** Where a bar ends: its due date, or its start when only that is set. */
function finish(issue: { start_date: string | null; due_date: string | null }): string | null {
  return issue.due_date ?? issue.start_date;
}

/** A bar's span in days, inclusive of both ends. Null unless both dates exist. */
function span(issue: { start_date: string | null; due_date: string | null }): number | null {
  if (!issue.start_date || !issue.due_date) return null;
  return daysBetween(issue.start_date, issue.due_date) + 1;
}

function maxDue(dues: Array<string | null>): string | null {
  // Date-only strings compare correctly as text — no parsing, no timezone.
  return dues.reduce<string | null>((best, d) => (d && (!best || d > best) ? d : best), null);
}

export function diffSnapshot(
  payload: ScheduleSnapshotPayload,
  currentIssues: PMIssue[]
): SnapshotDiff {
  const current = new Map(currentIssues.map((i) => [i.id, i]));
  const captured = new Map<number, SnapshotIssue>(payload.issues.map((i) => [i.id, i]));
  const entries: SnapshotDiffEntry[] = [];

  for (const was of payload.issues) {
    const now = current.get(was.id);
    if (!now) {
      entries.push({
        issue_id: was.id,
        issue_number: was.issue_number,
        title: was.title,
        kind: "removed",
        snapshotStart: was.start_date,
        snapshotDue: was.due_date,
        currentStart: null,
        currentDue: null,
        deltaDays: null,
        durationDeltaDays: null,
      });
      continue;
    }
    const from = finish(was);
    const to = finish(now);
    const wasSpan = span(was);
    const nowSpan = span(now);
    const moved = was.start_date !== now.start_date || was.due_date !== now.due_date;
    entries.push({
      issue_id: was.id,
      issue_number: was.issue_number,
      // The CURRENT title: the reader is looking at today's schedule and
      // asking what moved, not reading an archive.
      title: now.title,
      kind: moved ? "moved" : "unchanged",
      snapshotStart: was.start_date,
      snapshotDue: was.due_date,
      currentStart: now.start_date,
      currentDue: now.due_date,
      deltaDays: from && to ? daysBetween(from, to) : null,
      durationDeltaDays: wasSpan != null && nowSpan != null ? nowSpan - wasSpan : null,
    });
  }

  for (const now of currentIssues) {
    if (captured.has(now.id)) continue;
    entries.push({
      issue_id: now.id,
      issue_number: now.issue_number,
      title: now.title,
      kind: "added",
      snapshotStart: null,
      snapshotDue: null,
      currentStart: now.start_date,
      currentDue: now.due_date,
      deltaDays: null,
      durationDeltaDays: null,
    });
  }

  entries.sort((a, b) => a.issue_number - b.issue_number);

  const snapshotCompletion = maxDue(payload.issues.map((i) => i.due_date));
  const currentCompletion = maxDue(currentIssues.map((i) => i.due_date));

  return {
    entries,
    moved: entries.filter((e) => e.kind === "moved").length,
    added: entries.filter((e) => e.kind === "added").length,
    removed: entries.filter((e) => e.kind === "removed").length,
    unchanged: entries.filter((e) => e.kind === "unchanged").length,
    completion: {
      snapshot: snapshotCompletion,
      current: currentCompletion,
      deltaDays:
        snapshotCompletion && currentCompletion
          ? daysBetween(snapshotCompletion, currentCompletion)
          : null,
    },
  };
}

/** The diff keyed by issue, for annotating bars. */
export function diffByIssue(diff: SnapshotDiff): Map<number, SnapshotDiffEntry> {
  return new Map(diff.entries.map((e) => [e.issue_id, e]));
}

/**
 * A snapshot's issues shaped like live ones, so the same chart can draw them.
 * Fields the timeline does not read are filled with the snapshot's own values
 * or safe blanks — this is for rendering, never for writing.
 *
 * A captured discipline carries only its Estonian `name`. Where the live
 * catalogue still has it, that row is used instead, so an English viewer sees
 * the English name (and the badge its colours); one deleted since keeps the
 * captured name.
 */
export function snapshotIssuesAsPMIssues(
  payload: ScheduleSnapshotPayload,
  scope: { building_id: number; org_id: string; building_project_id: number },
  disciplineCatalogue: readonly Discipline[] = []
): PMIssue[] {
  const catalogue = new Map(disciplineCatalogue.map((d) => [d.id, d]));
  return payload.issues.map((i) => ({
    id: i.id,
    building_id: scope.building_id,
    org_id: scope.org_id,
    issue_number: i.issue_number,
    title: i.title,
    status: i.status,
    start_date: i.start_date,
    due_date: i.due_date,
    building_project_id: scope.building_project_id,
    protocol_row_id: null,
    parent_issue_id: i.parent_issue_id,
    created_at: payload.captured_at,
    created_by: 0,
    modified_at: null,
    modified_by: null,
    deleted_at: null,
    schedule_pinned: i.schedule_pinned,
    assignees: i.assignees.map((a) => ({
      id: a.id,
      first_name: a.name,
      last_name: "",
      email: "",
    })),
    disciplines: i.disciplines.map((d) => catalogue.get(d.id) ?? { id: d.id, name: d.name }),
  })) as PMIssue[];
}

/**
 * The comparison's rows: every issue on EITHER side, once, in reading order.
 *
 * Moved first, then added, then gone, then unchanged — so the chart and the
 * change list beside it run in the same order, and the rows that answer the
 * question sit at the top of both. Within a kind, by issue number.
 *
 * The chart drawn from these is deliberately ungrouped. Grouping was what made
 * the old side-by-side unreadable: an issue whose status changed landed in a
 * different band on each half, so row N stopped meaning the same issue on both.
 */
const KIND_ORDER: Record<SnapshotChangeKind, number> = {
  moved: 0,
  added: 1,
  removed: 2,
  unchanged: 3,
};

export function compareRows(diff: SnapshotDiff): SnapshotDiffEntry[] {
  return [...diff.entries].sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.issue_number - b.issue_number
  );
}

/** Did anything at all change between the snapshot and now? */
export function isUnchanged(diff: SnapshotDiff): boolean {
  return diff.moved === 0 && diff.added === 0 && diff.removed === 0;
}
